
import { Check, Plus, Globe, Instagram, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EventVenue } from "@/components/event-card/types";
import { cn } from "@/lib/utils";

interface VenueCardProps {
  venue: EventVenue;
  isInShortlist: boolean;
  onToggleShortlist: () => void;
  isProcessing?: boolean;
}

export const VenueCard = ({ 
  venue, 
  isInShortlist, 
  onToggleShortlist,
  isProcessing = false
}: VenueCardProps) => {
  return (
    <div className="h-[340px] w-full rounded-lg overflow-hidden border border-border flex flex-col">
      <div
        className="h-[160px] w-full bg-cover bg-center"
        style={{
          backgroundImage: venue.image_url 
            ? `url(${venue.image_url})` 
            : 'linear-gradient(to bottom right, #E5DEFF, #9b87f5)'
        }}
        aria-label={venue.image_url ? "Venue image" : "Venue image placeholder"}
      />
      <div className="p-4 space-y-3 flex-1 flex flex-col">
        <h3 className="font-semibold truncate">{venue.name}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{venue.address}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {venue.style && (
            <span className="px-2 py-1 bg-secondary rounded-full">{venue.style}</span>
          )}
          {venue.price_range && (
            <span className="px-2 py-1 bg-secondary rounded-full">{venue.price_range}</span>
          )}
        </div>
        
        {/* Venue links section */}
        <div className="flex items-center gap-2 mt-2">
          {venue.website_url && (
            <a 
              href={venue.website_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 transition-colors"
              title="Visit website"
            >
              <Globe size={16} />
            </a>
          )}
          {venue.instagram_handle && (
            <a 
              href={`https://instagram.com/${venue.instagram_handle.replace('@', '')}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 transition-colors"
              title="Visit Instagram"
            >
              <Instagram size={16} />
            </a>
          )}
          {venue.google_maps_link && (
            <a 
              href={venue.google_maps_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 transition-colors"
              title="View on Google Maps"
            >
              <MapPin size={16} />
            </a>
          )}
        </div>
        
        <div className="mt-auto pt-2">
          <Button
            onClick={onToggleShortlist}
            variant={isInShortlist ? "secondary" : "outline"}
            className={cn(
              "w-full mt-2",
              isInShortlist && "bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800",
              isProcessing && "opacity-70 cursor-not-allowed"
            )}
            disabled={isProcessing}
          >
            {isInShortlist ? (
              <>
                <Check size={18} />
                Added to Shortlist
              </>
            ) : (
              <>
                <Plus size={18} />
                Add to Shortlist
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
